import React, { useState, useEffect, Fragment, useRef } from "react";
import { Row, Col, Table, Pagination, Button, Form, InputGroup, ToggleButtonGroup, ToggleButton } from "react-bootstrap";
import * as moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import { showToast } from "../../Redux/toastReducer";
import apiService from "../../services/api.service";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import TableSearchBar from "../MyAccountComponents/TableSearchBar";

function CompanyWorkersTable({  }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company.company);
  const companyUser = useSelector((state) => state.company.companyUser);
  const pageSize = 10;
  const emptyWorker = { name: '', phone: '', email: '', passport: '', isVisible: false };

  const [newWorkerErrors, setNewWorkerErrors] = useState({});
  const [editWorkerErrors, setEditWorkerErrors] = useState({});
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);       // API page — triggers re-fetch
  const [pages, setPages] = useState(1);                   // total API pages (from totalCount)
  const [filteredCurrentPage, setFilteredCurrentPage] = useState(1); // client-side filter page
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [newWorker, setNewWorker] = useState(emptyWorker);
  const [editingWorkerId, setEditingWorkerId] = useState(null);
  const [editWorkerData, setEditWorkerData] = useState({});
  const fileInputRef = useRef(null);

  // true when the user has applied a search filter
  const isFiltered = filteredWorkers.length !== workers.length;
  const filteredPages = Math.ceil(filteredWorkers.length / pageSize);

  // active pagination values — switch between server and client modes
  const displayPage  = isFiltered ? filteredCurrentPage : currentPage;
  const displayPages = isFiltered ? filteredPages : pages;
  const setDisplayPage = isFiltered ? setFilteredCurrentPage : setCurrentPage;

  // rows to render — always slice by the active page
  const displayedWorkers = isFiltered
    ? filteredWorkers.slice((filteredCurrentPage - 1) * pageSize, filteredCurrentPage * pageSize)
    : workers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    getWorkers();
  }, []);

  useEffect(() => {
    setFilteredWorkers(workers);
    setFilteredCurrentPage(1);
  }, [workers]);

  const getWorkers = (mycustomerId) => {
    apiService.getCompanyWorkers(companyUser.companyId)
        .then((response) => {
          if (response.data && !response.data.HasErrors && response.data.data && response.data.data.length > 0) {
            setWorkers(response.data.data);
            setPages(Math.ceil(response.data.data.length / pageSize));
          } else {
            setWorkers([]);
          }
        },
        (error) => {}
      );
  }



  /* Rendering */

  const renderPaginationItems = () => {
    const items = [];

    for (let number = 1; number <= displayPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === displayPage}
          onClick={() => setDisplayPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return items;
  };

  const renderWorkers = (workers) => {
    return workers.map((currentWorker, index) => {
      const isEditing = currentWorker.workerId === editingWorkerId;
      const workerFullName = `${currentWorker.workerFirstName} ${currentWorker.workerLastName}`;

      if (isEditing) {
        return (
          <Fragment key={currentWorker.workerId || index}>
            <tr className="worker-item worker-item--editing">
              <td>
                <Form.Control type="text" placeholder={t("WorkerName")} value={`${editWorkerData.workerFirstName} ${editWorkerData.workerLastName}`}
                  isInvalid={!!editWorkerErrors.name}
                  onChange={(e) => { const parts = e.target.value.split(' '); setEditWorkerData(prev => ({ ...prev, workerFirstName: parts[0] || '', workerLastName: parts.slice(1).join(' ') })); }}
                />
                <Form.Control.Feedback type="invalid">{editWorkerErrors.name}</Form.Control.Feedback>
              </td>
              <td>
                <Form.Control type="tel" placeholder={t("WorkerPhone")} value={editWorkerData.workerPhone} 
                  isInvalid={!!editWorkerErrors.phone}
                  onChange={(e) => setEditWorkerData(prev => ({ ...prev, workerPhone: e.target.value }))}
                 />
                <Form.Control.Feedback type="invalid">{editWorkerErrors.phone}</Form.Control.Feedback>
              </td>
              <td>
                <Form.Control type="email" placeholder={t("Email")} value={editWorkerData.workerEmail}
                  isInvalid={!!editWorkerErrors.email}
                  onChange={(e) => setEditWorkerData(prev => ({ ...prev, workerEmail: e.target.value }))}
                />
                <Form.Control.Feedback type="invalid">{editWorkerErrors.email}</Form.Control.Feedback>
              </td>
              <td>
                <Form.Control type="text" placeholder={t("Passport")} value={editWorkerData.workerIdentification} onChange={(e) => setEditWorkerData(prev => ({ ...prev, workerIdentification: e.target.value }))} />
              </td>
              <td>
                <div className="row-btns">
                  <Button variant="secondary" className="account-btn row-btn-save" onClick={handleSaveEdit}><img src="../images/save-icon.svg" alt=""/></Button>
                  <Button variant="secondary" className="account-btn row-btn-delete" onClick={handleCancelEdit}><img src="../images/close-icon.svg" alt=""/></Button>
                </div>
              </td>
            </tr>
          </Fragment>
        );
      }

      // non-editing row
      return (
        <Fragment key={currentWorker.workerId || index}>
          <tr className="worker-item" worker-id={currentWorker.workerId}>
            <td aria-label={`${t("WorkerName")} ${workerFullName}`}>
              {workerFullName}
            </td>
            <td aria-label={`${t("WorkerPhone")} ${currentWorker.workerPhone}`}>
              {currentWorker.workerPhone}
            </td>
            <td aria-label={`${t("Email")} ${currentWorker.workerEmail}`}>
              {currentWorker.workerEmail}
            </td>
            <td aria-label={`${t("Passport")} ${currentWorker.workerIdentification}`}>
              {currentWorker.workerIdentification}
            </td>
            <td>
              <div className="row-btns">
                  <Button variant="secondary" className="account-btn row-btn-edit" onClick={() => handleEditWorker(currentWorker)}><img src="../images/edit-icon.svg" alt=""/></Button>
                  <Button variant="secondary" className="account-btn row-btn-delete" onClick={() => handleDeleteWorker(currentWorker)}><img src="../images/trash-icon.svg" alt=""/></Button>
              </div>
            </td>
          </tr>
        </Fragment>
      );
    });
  };


  /* Validation */

  const validateWorkerFields = ({ name, email, phone }) => {
    const errors = {};
    if (!(new RegExp(t("FullNameRegex")).test(name.trim()))) errors.name = t("FullNameRegexMsg");
    if (!(new RegExp(t("EmailRegex")).test(email))) errors.email = t("EmailRegexMsg");
    if (!(new RegExp(t("PhoneRegex")).test(phone))) errors.phone = t("PhoneRegexMsg");
    return errors;
  };


  /* Buttons Click */

  const handleWorkerSearch = () =>{
    setFilteredCurrentPage(1);
    const searchInput = document.getElementById("search-input-workers").value;

    const containsSearch = (value, search) => {
      if (value == null) return false;

      // string / number / boolean
      if ( typeof value === "string" || typeof value === "number" || typeof value === "boolean" ) {
        return value.toString().trim().toLowerCase().includes(search);
      }

      // array
      if (Array.isArray(value)) {
        return value.some(item => containsSearch(item, search));
      }

      // object
      if (typeof value === "object") {
        return Object.values(value).some(v =>
          containsSearch(v, search)
        );
      }

      return false;
    };

    setFilteredWorkers(workers.filter(item =>
      containsSearch(item, searchInput)
    ));
  }

  const handleToggleNewWorker = () => {
      if(newWorker.isVisible){
        setNewWorker(emptyWorker);
        setNewWorkerErrors({});
        return;
      }

      setNewWorker(prev => ({...prev, isVisible: true}));
  };

  const handleSaveNewWorker = () => {
    const errors = validateWorkerFields({ name: newWorker.name, email: newWorker.email, phone: newWorker.workerPhone });
    if (Object.keys(errors).length) { setNewWorkerErrors(errors); return; }
    setNewWorkerErrors({});

    let newWorkerReq = {
      "companyId": companyUser.companyId,
      "workerFirstName": newWorker.name.split(' ')[0] || '',
      "workerLastName": newWorker.name.split(' ')[1] || '',
      "workerIdentification": newWorker.passport,
      "workerEmail": newWorker.email,
      "workerPhone": newWorker.phone,
    }

    apiService.setCompanyWorker(newWorkerReq)
    .then((response) => {
        if (response.data && !response.data.HasErrors){
          setFilteredWorkers(prev => [{...newWorkerReq, workerId: response.data.data}, ...prev]);
          setNewWorker(emptyWorker);
          dispatch(showToast({ message: t("WorkerSaveSuccess"), severity: "success" }));
        } else {
          dispatch(showToast({ message: t("WorkerSaveFailure"), severity: "error" }));
        }
      },
      (error) => {
        dispatch(showToast({ message: t("WorkerSaveFailure"), severity: "error" }));
      }
    )
  }
  
  const handleDeleteWorker = (worker) => {
    if(!worker && !worker.workerId) {
      throw new Error("Worker id is required for deletion");
      return;
    }

    apiService.deleteCompanyWorker(companyUser.companyId, worker.workerId)
    .then((response) => {
        if (response.data && !response.data.HasErrors){
          setFilteredWorkers(prev => prev.filter(w => w.workerId !== worker.workerId));
          dispatch(showToast({ message: t("WorkerDeleteSuccess"), severity: "success" }));
        } else {
          dispatch(showToast({ message: t("WorkerDeleteFailure"), severity: "error" }));
        }
      },
      (error) => {
        dispatch(showToast({ message: t("WorkerDeleteFailure"), severity: "error" }));
      }
    )
  }

  const handleEditWorker = (worker) => {
    setEditingWorkerId(worker.workerId);
    setEditWorkerData({
      workerFirstName: worker.workerFirstName,
      workerLastName: worker.workerLastName,
      workerPhone: worker.workerPhone,
      workerEmail: worker.workerEmail,
      workerIdentification: worker.workerIdentification,
    });
  };

  const handleCancelEdit = () => {
    setEditingWorkerId(null);
    setEditWorkerData({});
    setEditWorkerErrors({});
  };

  const handleSaveEdit = () => {
    const name = `${editWorkerData.workerFirstName || ''} ${editWorkerData.workerLastName || ''}`;
    const errors = validateWorkerFields({ name, email: editWorkerData.workerEmail, phone: editWorkerData.workerPhone });
    if (Object.keys(errors).length) { setEditWorkerErrors(errors); return; }
    setEditWorkerErrors({});

    const updateReq = {
      companyId: companyUser.companyId,
      workerId: editingWorkerId,
      workerFirstName: editWorkerData.workerFirstName,
      workerLastName: editWorkerData.workerLastName,
      workerPhone: editWorkerData.workerPhone,
      workerEmail: editWorkerData.workerEmail,
      workerIdentification: editWorkerData.workerIdentification,
    };

    apiService.setCompanyWorker(updateReq)
      .then((response) => {
        if (response.data && !response.data.HasErrors) {
          setWorkers(prev => prev.map(w =>
            w.workerId === editingWorkerId ? { ...w, ...editWorkerData } : w
          ));
          setFilteredWorkers(prev => prev.map(w =>
            w.workerId === editingWorkerId ? { ...w, ...editWorkerData } : w
          ));
          handleCancelEdit();
          dispatch(showToast({ message: t("WorkerEditSuccess"), severity: "success" }));
        } else {
          dispatch(showToast({ message: t("WorkerEditFailure"), severity: "error" }));
        }
      },
      (error) => {
        dispatch(showToast({ message: t("WorkerEditFailure"), severity: "error" }));
      }
    );
  };


  const handleCreateExcelFile = () => {
    const headers = [["שם פרטי", "שם משפחה", "טלפון", "דואר אלקטרוני", "תעודת זהות"]];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Workers");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver(new Blob([buf], { type: "application/octet-stream" }), "workers_template.xlsx");
  };

  const handleUploadFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }).slice(1);
      let hasErrors = false;
      let invalidWorkerCount = 0;
      let workerCount = 0;

      for (const row of rows) {
        const [firstName, lastName, phone, email, passport] = row;
        if ( 
          (!firstName && !lastName) 
          || !(new RegExp(t("EmailRegex")).test(email))
          || workers.map(w => w.workerEmail).includes(email)
         ){
          invalidWorkerCount++;
          continue;
        }

        const req = {
          companyId: companyUser.companyId,
          workerFirstName: firstName || "",
          workerLastName: lastName || "",
          workerPhone: phone?.toString() || "",
          workerEmail: email || "",
          workerIdentification: passport?.toString() || "",
        };

        try {
          const response = await apiService.setCompanyWorker(req);

          if (response.data && !response.data.HasErrors) {
            setWorkers(prev => [{ ...req, workerId: response.data.data }, ...prev]);
            setFilteredWorkers(prev => [{ ...req, workerId: response.data.data }, ...prev]);
            workerCount++;
          }
        } catch (err) {
          hasErrors = false;
          console.log("worker upload error: ", err);
        }
      }

      if(workerCount > 0) {
        dispatch(showToast({ message: t("WorkersUploadFileSuccess").replace("{0}", workerCount) , severity: "success" }));
      }   
      if(hasErrors){
          dispatch(showToast({ message: t("WorkersUploadFileFailed").replace("{0}", workerCount), severity: "error" }));
      }
      if(invalidWorkerCount > 0){
          dispatch(showToast({ message: t("WorkersUploadFileInvalid").replace("{0}", invalidWorkerCount), severity: "error" }));
      }

    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <h4 aria-label={t("WorkersList")}>{t("WorkersList")}</h4>
      <Row>
          <Col className="flex-row d-flex gap-3">
            <div className="table-actions">
                <TableSearchBar dataList={workers} setFilteredDataList={setFilteredWorkers} onPageReset={() => setFilteredCurrentPage(1)} />
            
                <Button variant="secondary" className="account-btn row-btn-add" onClick={handleToggleNewWorker}><img src="../images/plus-icon.svg" alt=""/>{t("AddWorker")}</Button>
                <input type="file" accept=".xlsx,.xls" ref={fileInputRef} style={{ display: "none" }} onChange={handleUploadFile} />
                <Button variant="secondary" className="account-btn worker-btn-upload" onClick={() => fileInputRef.current.click()}><img src="../images/upload-icon.svg" alt=""/>{t("UploadFile")}</Button>
                <Button variant="secondary" className="account-btn btn-link worker-btn-file-create" onClick={handleCreateExcelFile}>{t("CreateExcelFile")}</Button>
                        
            </div>
          </Col>
      </Row>
      
      <Table hover className="workersTable" responsive>
        <thead>
          <tr>
            <th style={{width: "20%"}}>{t("WorkerName")}</th>
            <th style={{width: "15%"}}>{t("Phone")}</th>
            <th>{t("Email")}</th>
            <th style={{width: "15%"}}>{t("Passport")}</th>
            <th style={{width: "10%"}}> &nbsp; {/* for buttons */} </th> 
          </tr>
        </thead>
        <tbody>
          { 
            newWorker.isVisible &&
            <>
              <tr className="worker-item">
                <td aria-label={`${t("WorkerName")}`}>
                  <Form.Control type="text" placeholder={t("WorkerName")} value={newWorker.name} isInvalid={!!newWorkerErrors.name} onChange={(e) => setNewWorker(prev => ({ ...prev, name: e.target.value }))} />
                  <Form.Control.Feedback type="invalid">{newWorkerErrors.name}</Form.Control.Feedback>
                </td>
                <td aria-label={`${t("WorkerPhone")}`}>
                  <Form.Control type="tel" placeholder={t("WorkerPhone")} value={newWorker.phone} isInvalid={!!newWorkerErrors.phone} onChange={(e) => setNewWorker(prev => ({ ...prev, phone: e.target.value }))} />
                    <Form.Control.Feedback type="invalid">{newWorkerErrors.phone}</Form.Control.Feedback>
                </td>
                <td aria-label={`${t("Email")}`}>
                  <Form.Control type="email" placeholder={t("Email")} value={newWorker.email} isInvalid={!!newWorkerErrors.email} onChange={(e) => setNewWorker(prev => ({ ...prev, email: e.target.value }))} />
                  <Form.Control.Feedback type="invalid">{newWorkerErrors.email}</Form.Control.Feedback>
                </td>
                <td aria-label={`${t("Passport")}`}>
                  <Form.Control type="text" placeholder={t("Passport")} value={newWorker.passport} onChange={(e) => setNewWorker(prev => ({ ...prev, passport: e.target.value }))} />
                </td>
                <td>
                  <div className="row-btns">
                      <Button variant="secondary" className="account-btn row-btn-save" onClick={handleSaveNewWorker}><img src="../images/save-icon.svg" alt=""/></Button>
                      <Button variant="secondary" className="account-btn row-btn-delete" onClick={handleToggleNewWorker}><img src="../images/trash-icon.svg" alt=""/></Button>
                  </div>
                </td>
              </tr>
            </>
          }
          { renderWorkers(displayedWorkers) }
        </tbody>
      </Table>
      {!!filteredWorkers.length && (
        <Row className="justify-content-start">
          <Pagination className="justify-content-center">
            <Pagination.First disabled={displayPage === 1} onClick={() => setDisplayPage(1)} />
            <Pagination.Prev disabled={displayPage === 1} onClick={() => setDisplayPage(displayPage - 1)} />
            {renderPaginationItems()}
            <Pagination.Next disabled={displayPage === displayPages} onClick={() => setDisplayPage(displayPage + 1)} />
            <Pagination.Last disabled={displayPage === displayPages} onClick={() => setDisplayPage(displayPages)} />
          </Pagination>
        </Row>
      )}
    </>
  );
}

export default CompanyWorkersTable;
